import React, { Suspense, lazy, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { RouteSkeleton } from './components/RouteSkeleton';
import TenantLayout from './components/TenantLayout';
import AdminLayout from './components/AdminLayout';
import SubscriptionGuard from './components/SubscriptionGuard';
import { ThemeProvider } from './lib/theme/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { SETTINGS_ROUTES, INVOICE_ROUTES, PRODUCT_ROUTES } from './lib/constants/settingsRoutes';
import { ADMIN_ROUTES } from './lib/constants/adminRoutes';
import { PERMISSION_KEYS } from './lib/constants/permissionKeys';
import { PermissionGuard } from './components/PermissionGuard';
import { RoleGuard } from './components/RoleGuard';
import { USER_ROLES } from './lib/constants/roles';
import { homeRouteForRole } from './lib/constants/authRoutes';
import { FORBIDDEN_REASONS } from './lib/constants/forbiddenReasons';
import { useShallow } from 'zustand/react/shallow';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Dev-only: component playground — tree-shaken from production builds
const ComponentPlaygroundPage = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/ComponentPlaygroundPage'))
  : null;

// Admin pages
const OverviewPage = lazy(() => import('./pages/admin/OverviewPage'));
const TenantsPage = lazy(() => import('./pages/admin/TenantsPage'));
const TenantDetailPage = lazy(() => import('./pages/admin/TenantDetailPage'));
const ExchangeRatesPage = lazy(() => import('./pages/admin/ExchangeRatesPage'));
const MigrationsPage = lazy(() => import('./pages/admin/MigrationsPage'));
const StorageOverviewPage = lazy(() => import('./pages/admin/StorageOverviewPage'));
const StorageProfilesPage = lazy(() => import('./pages/admin/StorageProfilesPage'));
const SuperAdminEmailSettingsPage = lazy(() => import('./pages/admin/EmailSettingsPage'));
const SuperAdminEmailTemplatesPage = lazy(() => import('./pages/admin/EmailTemplatesPage'));
const AppCatalogSuggestionsPage = lazy(() => import('./pages/admin/AppCatalogSuggestionsPage'));
const AppCatalogRoadmapPage = lazy(() => import('./pages/admin/AppCatalogRoadmapPage'));
const AppStoreCatalogPage = lazy(() => import('./pages/admin/AppStoreCatalogPage'));
const CommercialPoliciesPage = lazy(() => import('./pages/admin/CommercialPoliciesPage'));
const PackagingPage = lazy(() => import('./pages/admin/PackagingPage'));
const AdminMarketplaceStoresPage = lazy(() => import('./pages/admin/MarketplaceStoresPage'));
const MarketplaceCatalogSyncPage = lazy(() => import('./pages/admin/MarketplaceCatalogSyncPage'));
const BillingSettingsPage = lazy(() => import('./pages/admin/BillingSettingsPage'));

// Tenant pages
const CategoriesPage = lazy(() => import('./pages/tenant/CategoriesPage'));
const BrandsPage = lazy(() => import('./pages/tenant/BrandsPage'));
const AttributesPage = lazy(() => import('./pages/tenant/AttributesPage'));
const ProductsPage = lazy(() => import('./pages/tenant/products-list/ProductsListPage'));
const ProductFormPage = lazy(() => import('./pages/tenant/products/ProductFormPage'));
const StockReceiptsPage = lazy(() => import('./pages/tenant/StockReceiptsPage'));
const StockMovementsPage = lazy(() => import('./pages/tenant/StockMovementsPage'));
const DashboardPage = lazy(() => import('./pages/tenant/DashboardPage'));
const ProfilePage   = lazy(() => import('./pages/tenant/ProfilePage'));
const WarehousesPage = lazy(() => import('./pages/tenant/WarehousesPage'));
const TenantUsersPage = lazy(() => import('./pages/tenant/TenantUsersPage'));
const CurrentAccountsPage = lazy(() => import('./pages/tenant/CurrentAccountsPage'));
const CashBankPage = lazy(() => import('./pages/tenant/CashBankPage'));
const ExpensesPage = lazy(() => import('./pages/tenant/ExpensesPage'));
const ExpenseCategoriesPage = lazy(() => import('./pages/tenant/settings/ExpenseCategoriesPage'));
const ExchangeRatesViewPage = lazy(() => import('./pages/tenant/ExchangeRatesViewPage'));
const PriceListPage = lazy(() => import('./pages/tenant/PriceListPage'));
const OrdersPage = lazy(() => import('./pages/tenant/OrdersPage'));
const ReportsHubPage = lazy(() => import('./pages/tenant/reports/ReportsHubPage'));
const ReportsPage = lazy(() => import('./pages/tenant/reports/ReportsPage'));
const CustomerSalesReportPage = lazy(() => import('./pages/tenant/reports/CustomerSalesReportPage'));
const ProductSalesReportPage = lazy(() => import('./pages/tenant/reports/ProductSalesReportPage'));
const MarketplaceSalesReportPage = lazy(() => import('./pages/tenant/reports/MarketplaceSalesReportPage'));
const CustomerStatementReportPage = lazy(() => import('./pages/tenant/reports/CustomerStatementReportPage'));
const StockStatusReportPage = lazy(() => import('./pages/tenant/reports/StockStatusReportPage'));
const LowStockReportPage = lazy(() => import('./pages/tenant/reports/LowStockReportPage'));
const AgingReportPage = lazy(() => import('./pages/tenant/reports/AgingReportPage'));
const PurchaseBySupplierReportPage = lazy(() => import('./pages/tenant/reports/PurchaseBySupplierReportPage'));
const PurchaseByProductReportPage = lazy(() => import('./pages/tenant/reports/PurchaseByProductReportPage'));
const CashBankStatusReportPage = lazy(() => import('./pages/tenant/reports/CashBankStatusReportPage'));
const CashBankStatementReportPage = lazy(() => import('./pages/tenant/reports/CashBankStatementReportPage'));
const StockLedgerReportPage = lazy(() => import('./pages/tenant/reports/StockLedgerReportPage'));
const CashflowReportPage = lazy(() => import('./pages/tenant/reports/CashflowReportPage'));
const VatSummaryReportPage = lazy(() => import('./pages/tenant/reports/VatSummaryReportPage'));
const StockCountVarianceReportPage = lazy(() => import('./pages/tenant/reports/StockCountVarianceReportPage'));
const UnfulfillableAllocationsReportPage = lazy(() => import('./pages/tenant/reports/UnfulfillableAllocationsReportPage'));
const SimplePnlReportPage = lazy(() => import('./pages/tenant/reports/SimplePnlReportPage'));
const SupplierPerformanceReportPage = lazy(() => import('./pages/tenant/reports/SupplierPerformanceReportPage'));
const OrderFormPage = lazy(() => import('./pages/tenant/orders/OrderFormPage'));
const InvoicesPage = lazy(() => import('./pages/tenant/invoices/InvoicesListPage'));
const AppSettingsPage = lazy(() => import('./pages/tenant/settings/AppSettingsPage'));
const InvoiceSeriesPage = lazy(() => import('./pages/tenant/InvoiceSeriesPage'));
const InvoicePrintPage = lazy(() => import('./pages/tenant/InvoicePrintPage'));
const DeliveryNotesPage = lazy(() => import('./pages/tenant/DeliveryNotesPage'));
const DeliveryNotePrintPage = lazy(() => import('./pages/tenant/DeliveryNotePrintPage'));
const LegacyDeliveryNotePrintPage = lazy(() => import('./pages/tenant/LegacyDeliveryNotePrintPage'));
const OrderLabelPrintPage = lazy(() => import('./pages/tenant/OrderLabelPrintPage'));
const OrderReceiptPrintPage = lazy(() => import('./pages/tenant/OrderReceiptPrintPage'));
const PrintTemplateDesignerPrintPage = lazy(() => import('./pages/tenant/PrintTemplateDesignerPrintPage'));
const QuotesPage = lazy(() => import('./pages/tenant/QuotesPage'));
const QuoteFormPage = lazy(() => import('./pages/tenant/quotes/QuoteFormPage'));
const CheckNotesPage = lazy(() => import('./pages/tenant/CheckNotesPage'));
const MarketplaceStoresPage = lazy(() => import('./pages/tenant/marketplaces/MarketplacesPage'));
const MarketplaceSyncPage = lazy(() => import('./pages/tenant/MarketplaceSyncPage'));
const ShopifyConnectPage = lazy(() => import('./pages/tenant/marketplaces/ShopifyConnectPage'));
const ProductMatchingPage = lazy(() => import('./pages/tenant/ProductMatchingPage'));
const TenantAppsPage = lazy(() => import('./pages/tenant/TenantAppsPage'));
const AuditLogsPage = lazy(() => import('./pages/tenant/AuditLogsPage'));
const MediaLibraryPage = lazy(() => import('./pages/tenant/MediaLibraryPage'));
const ImportsPage = lazy(() => import('./pages/tenant/imports/ImportsPage'));
const UnresolvedOrderItemsPage = lazy(() => import('./pages/tenant/UnresolvedOrderItemsPage'));

// e-Fatura pages
const EfaturaSettingsPage = lazy(() => import('./pages/tenant/efatura/EfaturaSettingsPage'));
const EfaturaLogsPage = lazy(() => import('./pages/tenant/efatura/EfaturaLogsPage'));
const EfaturaLogDetailPage = lazy(() => import('./pages/tenant/efatura/EfaturaLogDetailPage'));
const EfaturaInboundPage = lazy(() => import('./pages/tenant/efatura/EfaturaInboundPage'));

// Settings hub
const SettingsLayout = lazy(() => import('./pages/tenant/settings/SettingsLayout'));
const BillingPage = lazy(() => import('./pages/tenant/settings/BillingPage'));
const BillingReturnPage = lazy(() => import('./pages/tenant/settings/billing/BillingReturnPage'));
const LocalizationPage = lazy(() => import('./pages/tenant/settings/LocalizationPage'));
const OperationsDefaultsPage = lazy(() => import('./pages/tenant/settings/OperationsDefaultsPage'));
const FxSettingsPage = lazy(() => import('./pages/tenant/settings/FxSettingsPage'));
const CompaniesListPage = lazy(() => import('./pages/tenant/settings/CompaniesListPage'));
const CompanyDetailPage = lazy(() => import('./pages/tenant/settings/CompanyDetailPage'));
const DocumentNumberingPage = lazy(() => import('./pages/tenant/settings/DocumentNumberingPage'));
const PermissionsMatrixPage = lazy(() => import('./pages/tenant/settings/PermissionsMatrixPage'));
const TenantEmailSettingsPage = lazy(() => import('./pages/tenant/settings/EmailSettingsPage'));
const TenantEmailTemplatesPage = lazy(() => import('./pages/tenant/settings/EmailTemplatesPage'));
const PrintTemplatesPage = lazy(() => import('./pages/tenant/settings/print-templates/PrintTemplatesPage'));
const PrintTemplateDesignerPage = lazy(() => import('./pages/tenant/settings/print-templates/PrintTemplateDesignerPage'));

const TENANT_ROLES = [USER_ROLES.TENANT_ADMIN, USER_ROLES.TENANT_MEMBER, USER_ROLES.SUPER_ADMIN] as const;
const ADMIN_ROLES = [USER_ROLES.SUPER_ADMIN] as const;
const TENANT_ADMIN_ONLY = [USER_ROLES.TENANT_ADMIN] as const;

/**
 * Redirect that keeps the query string. Plain `<Navigate to="/x" />` drops it, which is fine for
 * the legacy redirects below but not for the Shopify handoff: that URL carries `?status=` and the
 * page renders a different screen for each value. Losing it turns a completed install into the
 * generic failure state, on a route the merchant cannot re-enter (the install cookie is single-use).
 */
const RedirectPreservingQuery = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};

/**
 * Sign-in/sign-up screens: an authenticated visitor goes home instead. While the startup session
 * restore is in flight the outcome is unknown, so neither the form nor the redirect is rendered.
 */
const GuestOnlyRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, user, sessionRestoring } = useAuthStore(useShallow((s) => ({ isAuthenticated: s.isAuthenticated, user: s.user, sessionRestoring: s.sessionRestoring })));
  if (sessionRestoring) return <RouteSkeleton />;
  if (isAuthenticated) return <Navigate to={homeRouteForRole(user?.role)} replace />;
  return children;
};

// Wraps Routes so Sentry names transactions by route pattern. Evaluated at import time, so Sentry must
// already be initialised (main.tsx imports services/sentryBootstrap first); otherwise this is plain Routes.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

function App() {

  return (
    <ThemeProvider>
    <TooltipProvider>
    <Toaster />
    <BrowserRouter>
      {/* Outer Suspense: default skeleton for any route not wrapped below */}
      <Suspense fallback={<RouteSkeleton />}>
        <SentryRoutes>
          {/* Dev-only: component playground — outside all auth/layout guards */}
          {import.meta.env.DEV && ComponentPlaygroundPage && (
            <Route path="/dev/ui" element={<ComponentPlaygroundPage />} />
          )}

          {/* Public */}
          <Route
            path="/login"
            element={<GuestOnlyRoute><LoginPage /></GuestOnlyRoute>}
          />
          <Route
            path="/signup"
            element={<GuestOnlyRoute><SignupPage /></GuestOnlyRoute>}
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/activate"
            element={<ResetPasswordPage mode="activate" />}
          />

          {/* SuperAdmin routes — AdminLayout mounts once across all child routes */}
          <Route element={<RoleGuard allowedRoles={ADMIN_ROLES} forbiddenReason={FORBIDDEN_REASONS.superAdminOnly} />}>
            <Route element={<AdminLayout />}>
              <Route path="/super-admin" element={<OverviewPage />} />
              <Route path="/super-admin/tenants" element={<TenantsPage />} />
              <Route path="/super-admin/tenants/:id" element={<TenantDetailPage />} />
              <Route path="/super-admin/exchange-rates" element={
                <Suspense fallback={<RouteSkeleton.Form />}><ExchangeRatesPage /></Suspense>
              } />
              <Route path="/super-admin/storage-migrations" element={<MigrationsPage />} />
              <Route path="/super-admin/storage-overview" element={<StorageOverviewPage />} />
              <Route path="/super-admin/storage-profiles" element={<StorageProfilesPage />} />
              <Route path="/super-admin/email-settings" element={<SuperAdminEmailSettingsPage />} />
              <Route path={ADMIN_ROUTES.billingSettings} element={<BillingSettingsPage />} />
              <Route path="/super-admin/email-templates" element={<SuperAdminEmailTemplatesPage />} />
              <Route path="/super-admin/app-catalog/suggestions" element={<AppCatalogSuggestionsPage />} />
              <Route path="/super-admin/app-catalog/roadmap" element={<AppCatalogRoadmapPage />} />
              <Route path="/super-admin/appstore" element={<AppStoreCatalogPage />} />
              <Route path="/super-admin/appstore/policies" element={<CommercialPoliciesPage />} />
              <Route path="/super-admin/appstore/packaging" element={<PackagingPage />} />
              <Route path={ADMIN_ROUTES.marketplaceStores} element={
                <Suspense fallback={<RouteSkeleton.Table />}><AdminMarketplaceStoresPage /></Suspense>
              } />
              <Route path="/super-admin/marketplace-catalog" element={
                <Suspense fallback={<RouteSkeleton.Table />}><MarketplaceCatalogSyncPage /></Suspense>
              } />
            </Route>
          </Route>

          {/* Tenant routes — TenantLayout mounts once across all child routes */}
          <Route element={<RoleGuard allowedRoles={TENANT_ROLES} forbiddenReason={FORBIDDEN_REASONS.permission} />}>
            {/* Outside TenantLayout on purpose: a Blocked tenant gets the full-screen
                BlockedAccessPage instead of the sidebar shell (docs/design/billing-access-gate.md). */}
            <Route element={<SubscriptionGuard />}>
            <Route element={<TenantLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/dashboard/profile" element={
                <Suspense fallback={<RouteSkeleton.Form />}><ProfilePage /></Suspense>
              } />
              {/* Catalog — Categories, Brands, Attributes each gated by their Read key */}
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.CategoriesRead} />}>
                <Route path="/dashboard/categories" element={<CategoriesPage />} />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.BrandsRead} />}>
                <Route path="/dashboard/brands" element={<BrandsPage />} />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.AttributesRead} />}>
                <Route path="/dashboard/attributes" element={<AttributesPage />} />
              </Route>

              {/* Table pages — show table-shaped skeleton while the chunk loads */}
              {/* Ürün eşleştirme ayarlardan katalog altına taşındı. İzin kapısı bilerek
                  aynı kaldı ve Ürünler bloğunun dışında duruyor: sayfa pazaryeri
                  bağlantılarının içini gösteriyor, konumunun değişmesi yetki yüzeyini
                  ne genişletmeli ne daraltmalı. */}
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsIntegrationsMarketplacesRead} />}>
                <Route path={PRODUCT_ROUTES.matching} element={
                  <Suspense fallback={<RouteSkeleton.Table />}><ProductMatchingPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.ProductsRead} />}>
                <Route path="/dashboard/products" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><ProductsPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.OrdersRead} />}>
                <Route path="/dashboard/orders" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><OrdersPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.ReportsRead} />}>
                <Route path="/dashboard/reports" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><ReportsHubPage /></Suspense>
                } />
                <Route path="/dashboard/reports/sales-by-period" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><ReportsPage /></Suspense>
                } />
                <Route path="/dashboard/reports/customers" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><CustomerSalesReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/products" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><ProductSalesReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/marketplaces" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><MarketplaceSalesReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/statement" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><CustomerStatementReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/stock-status" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><StockStatusReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/low-stock" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><LowStockReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/aging" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><AgingReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/purchase-by-supplier" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><PurchaseBySupplierReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/purchase-by-product" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><PurchaseByProductReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/cash-bank-status" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><CashBankStatusReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/cash-bank-statement" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><CashBankStatementReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/stock-ledger" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><StockLedgerReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/cashflow" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><CashflowReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/vat-summary" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><VatSummaryReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/stock-count-variance" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><StockCountVarianceReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/unfulfillable-allocations" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><UnfulfillableAllocationsReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/simple-pnl" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><SimplePnlReportPage /></Suspense>
                } />
                <Route path="/dashboard/reports/supplier-performance" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><SupplierPerformanceReportPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.QuotesRead} />}>
                <Route path="/dashboard/quotes" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><QuotesPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.CashBankRead} />}>
                <Route path="/dashboard/cash-bank" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><CashBankPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.CurrentAccountsRead} />}>
                <Route path="/dashboard/accounts" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><CurrentAccountsPage /></Suspense>
                } />
              </Route>
              {/* AB#1017 — Expenses: allow-listed until ExpensesRead permission key lands (FU/allow-list #1011). */}
              <Route path="/dashboard/expenses" element={
                <Suspense fallback={<RouteSkeleton.Table />}><ExpensesPage /></Suspense>
              } />
              {/* /dashboard/marketplace-stores redirects to settings — see legacy redirects below */}

              {/* Form pages — show form-shaped skeleton while the chunk loads */}
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.ProductsRead} />}>
                <Route path="/dashboard/products/new" element={
                  <Suspense fallback={<RouteSkeleton.Form />}><ProductFormPage /></Suspense>
                } />
                <Route path="/dashboard/products/:id" element={
                  <Suspense fallback={<RouteSkeleton.Form />}><ProductFormPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.OrdersRead} />}>
                <Route path="/dashboard/orders/new" element={
                  <Suspense fallback={<RouteSkeleton.Form />}><OrderFormPage /></Suspense>
                } />
                <Route path="/dashboard/orders/:id/edit" element={
                  <Suspense fallback={<RouteSkeleton.Form />}><OrderFormPage /></Suspense>
                } />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.QuotesRead} />}>
                <Route path="/dashboard/quotes/new" element={
                  <Suspense fallback={<RouteSkeleton.Form />}><QuoteFormPage /></Suspense>
                } />
                <Route path="/dashboard/quotes/:id/edit" element={
                  <Suspense fallback={<RouteSkeleton.Form />}><QuoteFormPage /></Suspense>
                } />
              </Route>

              {/* Default-fallback pages */}
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.StockReceiptsRead} />}>
                <Route path="/dashboard/stock-receipts" element={<StockReceiptsPage />} />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.StockReceiptsRead} />}>
                <Route path="/dashboard/stock-movements" element={<StockMovementsPage />} />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.WarehouseRead} />}>
                <Route path="/dashboard/warehouses" element={<WarehousesPage />} />
              </Route>
              {/* allow-listed: no permission key yet — see FU/allow-list (#1011) */}
              <Route path="/dashboard/price-lists" element={<PriceListPage />} />
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.CheckNotesRead} />}>
                <Route path="/dashboard/check-notes" element={<CheckNotesPage />} />
              </Route>
              {/* Faturalar bölümü: giden faturalar kök sayfa, gelen faturalar ve
                  seriler onun sekmeleri. Sidebar tek bir girdi taşır. */}
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.InvoicesRead} />}>
                <Route path={INVOICE_ROUTES.root} element={<InvoicesPage />} />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsIntegrationsEfaturaRead} />}>
                <Route path={INVOICE_ROUTES.inbound} element={
                  <Suspense fallback={<RouteSkeleton.Table />}><EfaturaInboundPage /></Suspense>
                } />
              </Route>
              <Route element={<RoleGuard allowedRoles={TENANT_ADMIN_ONLY} forbiddenReason={FORBIDDEN_REASONS.tenantAdminOnly} />}>
                <Route path={INVOICE_ROUTES.series} element={<InvoiceSeriesPage />} />
              </Route>
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.DeliveryNotesRead} />}>
                <Route path="/dashboard/delivery-notes" element={<DeliveryNotesPage />} />
              </Route>
              {/* allow-listed: no permission key yet — see FU/allow-list (#1011) */}
              <Route path="/dashboard/media" element={<MediaLibraryPage />} />
              {/* No route-level PermissionGuard on purpose: this page spans two resources
                  (Products.Write for urun + acilis-stok, CurrentAccounts.Write for cari) and
                  PermissionGuard takes a single key. Guarding on either one bounces a user who
                  legitimately holds the other: TenantLayout shows the nav item on
                  Products.Write || CurrentAccounts.Write, so a cari-only user would click a
                  visible link and land on a redirect. ImportsPage does the real per-tab gating
                  and renders a denied state when neither applies; the backend default-denies
                  independently (CLAUDE.md: a UI gate is UX, never the enforcement point). */}
              <Route path="/dashboard/imports" element={
                <Suspense fallback={<RouteSkeleton.Table />}><ImportsPage /></Suspense>
              } />
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.MarketplaceRead} />}>
                <Route path="/dashboard/marketplace-sync" element={<MarketplaceSyncPage />} />
              </Route>
              {/* /dashboard/apps and /dashboard/product-matching redirect to settings — see legacy redirects below */}
              <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.OrdersRead} />}>
                <Route path="/dashboard/unresolved-order-items" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><UnresolvedOrderItemsPage /></Suspense>
                } />
              </Route>

              {/* Settings hub — SettingsLayout provides the rail + outlet */}
              <Route
                path="/dashboard/settings"
                element={
                  <Suspense fallback={<RouteSkeleton />}>
                    <SettingsLayout />
                  </Suspense>
                }
              >
                <Route index element={<Navigate to={SETTINGS_ROUTES.localization} replace />} />

                {/* General (placeholder) — each sub-route gated by its own permission */}
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsLocalizationRead} />}>
                  <Route path="localization" element={
                    <Suspense fallback={<RouteSkeleton.Form />}><LocalizationPage /></Suspense>
                  } />
                </Route>
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsOperationsDefaultsRead} />}>
                  <Route path="operations-defaults" element={
                    <Suspense fallback={<RouteSkeleton.Form />}><OperationsDefaultsPage /></Suspense>
                  } />
                </Route>
                {/* Döviz bölümü: sağlayıcı ayarı ve onun ürettiği günlük kur tablosu
                    aynı sekme şeridinin iki yarısı. */}
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsFxRead} />}>
                  <Route path="fx" element={
                    <Suspense fallback={<RouteSkeleton.Form />}><FxSettingsPage /></Suspense>
                  } />
                  <Route path="fx/rates" element={
                    <Suspense fallback={<RouteSkeleton.Table />}><ExchangeRatesViewPage /></Suspense>
                  } />
                </Route>
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsEmailRead} />}>
                  <Route path="email" element={
                    <Suspense fallback={<RouteSkeleton />}><TenantEmailSettingsPage /></Suspense>
                  } />
                  <Route path="email-templates" element={
                    <Suspense fallback={<RouteSkeleton />}><TenantEmailTemplatesPage /></Suspense>
                  } />
                </Route>
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsCompanyRead} />}>
                  <Route path="companies" element={
                    <Suspense fallback={<RouteSkeleton.Table />}><CompaniesListPage /></Suspense>
                  } />
                  <Route path="companies/:companyId" element={
                    <Suspense fallback={<RouteSkeleton.Form />}><CompanyDetailPage /></Suspense>
                  } />
                </Route>
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsDocumentNumberingRead} />}>
                  <Route path="document-numbering" element={
                    <Suspense fallback={<RouteSkeleton.Form />}><DocumentNumberingPage /></Suspense>
                  } />
                </Route>
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.PrintTemplatesRead} />}>
                  <Route path="print-templates" element={
                    <Suspense fallback={<RouteSkeleton.Table />}><PrintTemplatesPage /></Suspense>
                  } />
                  <Route path="print-templates/new" element={
                    <Suspense fallback={<RouteSkeleton />}><PrintTemplateDesignerPage /></Suspense>
                  } />
                  <Route path="print-templates/:id" element={
                    <Suspense fallback={<RouteSkeleton />}><PrintTemplateDesignerPage /></Suspense>
                  } />
                </Route>
                {/* Gider kategorileri ayarlar menüsünde listeleniyordu ama route'u
                    SettingsLayout'un dışındaydı: sayfa açıldığında ayarlar raili
                    kayboluyordu. Artık kabuğun içinde.
                    allow-listed: no permission key yet — see FU/allow-list (#1011) */}
                <Route path="expense-categories" element={
                  <Suspense fallback={<RouteSkeleton.Table />}><ExpenseCategoriesPage /></Suspense>
                } />

                {/* Integrations — each sub-route gated by its own permission */}
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsIntegrationsMarketplacesRead} />}>
                  <Route path="integrations/marketplaces" element={
                    <Suspense fallback={<RouteSkeleton.Table />}><MarketplaceStoresPage /></Suspense>
                  } />
                </Route>
                {/* AB#1864/AB#1889: the Shopify OAuth handoff lands here, inside the settings
                    shell, because SettingsRail derives its active item from the pathname by
                    prefix. Served from outside /dashboard/settings the merchant would get the
                    rail with nothing selected, having just left the marketplaces page the flow
                    returns them to. The path must stay byte-for-byte identical to
                    ShopifyOAuthEndpoints.FrontendConnectPath.

                    Deliberately its OWN guard rather than the SettingsIntegrationsMarketplacesRead
                    block above: this page is reached from an external redirect, not from settings
                    navigation, so the settings key is the wrong question to ask about it.

                    Marketplace.Read is the entry bar on purpose, and it is deliberately LOWER than
                    what the page's action needs: install-session is a GET (Read), but claim is a
                    POST and the group's ResourcePermission maps POST to Marketplace.Write. A
                    read-only user therefore reaches the screen and is refused at the Bagla button
                    by the server. That is the intended shape here rather than a 403 page: the
                    merchant arrives holding a single-use install cookie, and a hard bounce would
                    burn it with nothing on screen explaining why. */}
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.MarketplaceRead} />}>
                  <Route path="integrations/marketplaces/shopify/connect" element={
                    <Suspense fallback={<RouteSkeleton />}><ShopifyConnectPage /></Suspense>
                  } />
                </Route>
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsIntegrationsEfaturaRead} />}>
                  <Route path="integrations/efatura" element={
                    <Suspense fallback={<RouteSkeleton.Form />}><EfaturaSettingsPage /></Suspense>
                  } />
                  <Route path="integrations/efatura/logs" element={
                    <Suspense fallback={<RouteSkeleton.Table />}><EfaturaLogsPage /></Suspense>
                  } />
                  <Route path="integrations/efatura/logs/:id" element={
                    <Suspense fallback={<RouteSkeleton.Form />}><EfaturaLogDetailPage /></Suspense>
                  } />
                </Route>

                {/* Security — TenantAdmin-only (RoleGuard) + per-route permission */}
                <Route element={<RoleGuard allowedRoles={TENANT_ADMIN_ONLY} forbiddenReason={FORBIDDEN_REASONS.tenantAdminOnly} />}>
                  <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsSecurityUsersRead} />}>
                    <Route path="security/users" element={<TenantUsersPage />} />
                  </Route>
                  <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsSecurityAuditLogsRead} />}>
                    <Route path="security/audit-logs" element={<AuditLogsPage />} />
                  </Route>
                  <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsSecurityPermissionsRead} />}>
                    <Route path="security/permissions" element={
                      <Suspense fallback={<RouteSkeleton.Table />}><PermissionsMatrixPage /></Suspense>
                    } />
                  </Route>
                </Route>

                {/* Apps — katalog + uygulama başına ayar sayfası. Pazaryeri
                    mağazaları ve e-Fatura ayarları artık ayrı menü satırları
                    değil, ilgili uygulamanın kartından açılıyor. */}
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.SettingsAppsRead} />}>
                  <Route path="apps" element={<TenantAppsPage />} />
                  <Route path="apps/:appCode" element={
                    <Suspense fallback={<RouteSkeleton />}><AppSettingsPage /></Suspense>
                  } />
                </Route>

                {/* Billing — Billing.Read for all; manage actions gated on TenantAdmin role in-page */}
                <Route element={<PermissionGuard requiredPermission={PERMISSION_KEYS.BillingRead} />}>
                  <Route path="billing" element={
                    <Suspense fallback={<RouteSkeleton />}><BillingPage /></Suspense>
                  } />
                  <Route path="billing/return" element={
                    <Suspense fallback={<RouteSkeleton />}><BillingReturnPage /></Suspense>
                  } />
                </Route>
              </Route>

              {/* Legacy redirects — kept for 1 release cycle */}
              <Route path="/dashboard/settings/company-profile" element={<Navigate to={SETTINGS_ROUTES.companies} replace />} />
              <Route path="/dashboard/apps" element={<Navigate to={SETTINGS_ROUTES.apps} replace />} />
              {/* Outlives the one-release window: renewal-reminder notifications already stored in the
                  database carry this old path as their link, so the redirect must live as long as
                  those rows do. Removing it sends them silently to /dashboard again, and the
                  notification route guardian only checks links issued by current code. */}
              <Route path="/dashboard/subscriptions" element={<Navigate to={SETTINGS_ROUTES.billing} replace />} />
              <Route path="/dashboard/marketplace-stores" element={<Navigate to={SETTINGS_ROUTES.integrations.marketplaces} replace />} />
              {/* AB#1889: the connect page moved into the settings shell. This redirect must outlive
                  the usual one-release window, because a merchant whose OAuth round-trip started
                  before the deploy comes back to the old path.

                  It keeps the query string because the page renders a different screen per
                  ?status= value; losing it turns a completed install into the generic failure
                  state. The install cookie is NOT at risk either way: __Host- forces Path=/, so it
                  is sent regardless of which SPA route the browser lands on. */}
              <Route path="/dashboard/marketplaces/shopify/connect" element={
                <RedirectPreservingQuery to={SETTINGS_ROUTES.integrations.shopifyConnect} />
              } />
              <Route path="/dashboard/efatura/settings" element={<Navigate to={SETTINGS_ROUTES.integrations.efatura} replace />} />
              <Route path="/dashboard/efatura/logs" element={<Navigate to={SETTINGS_ROUTES.integrations.efaturaLogs} replace />} />
              <Route path="/dashboard/efatura/logs/:id" element={<Navigate to={SETTINGS_ROUTES.integrations.efaturaLogs} replace />} />
              <Route path="/dashboard/efatura/gelen" element={<Navigate to={INVOICE_ROUTES.inbound} replace />} />
              <Route path="/dashboard/settings/integrations/efatura/inbound" element={<Navigate to={INVOICE_ROUTES.inbound} replace />} />
              <Route path="/dashboard/exchange-rates" element={<Navigate to={SETTINGS_ROUTES.fxRates} replace />} />
              <Route path="/dashboard/product-matching" element={<Navigate to={PRODUCT_ROUTES.matching} replace />} />
              <Route path={SETTINGS_ROUTES.integrations.productMatching} element={<Navigate to={PRODUCT_ROUTES.matching} replace />} />
              <Route path="/dashboard/users" element={<Navigate to={SETTINGS_ROUTES.security.users} replace />} />
              <Route path="/dashboard/audit-logs" element={<Navigate to={SETTINGS_ROUTES.security.auditLogs} replace />} />

              <Route path="/dashboard/invoice-series" element={<Navigate to={INVOICE_ROUTES.series} replace />} />
              <Route path="/dashboard/inbound-invoices" element={<Navigate to={INVOICE_ROUTES.inbound} replace />} />
            </Route>
            </Route>

            {/* Print pages sit outside both TenantLayout and SubscriptionGuard so the print
                view itself renders without the sidebar chrome for a non-Blocked tenant. This
                does NOT make printing work while Blocked: TenantAccessGateMiddleware's
                allowlist only covers /billing* and /apps* (docs/design/billing-access-gate.md),
                so a Blocked tenant reaching one of these routes still gets 402 on the
                underlying data fetch (e.g. GET the invoice to print). A reachable SPA route is
                not the same as a succeeding API call. */}
            <Route path="/dashboard/invoices/:id/print" element={<InvoicePrintPage />} />
            <Route path="/dashboard/invoices/print" element={<InvoicePrintPage />} />
            <Route path="/dashboard/delivery-notes/:id/print" element={<DeliveryNotePrintPage />} />
            <Route path="/dashboard/delivery-notes/print" element={<DeliveryNotePrintPage />} />
            {/* Fallback to the pre-AB#2447 fixed page for one release; removed by AB#2250. */}
            <Route path="/dashboard/delivery-notes/:id/print/legacy" element={<LegacyDeliveryNotePrintPage />} />
            <Route path="/dashboard/orders/labels/print" element={<OrderLabelPrintPage />} />
            <Route path="/dashboard/orders/receipts/print" element={<OrderReceiptPrintPage />} />
            <Route path="/dashboard/print-templates/:id/print" element={<PrintTemplateDesignerPrintPage />} />
          </Route>

          {/* Default */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </SentryRoutes>
      </Suspense>
    </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
